import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Grid, Typography, CircularProgress } from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";

import { ServerFailed } from "../errors/serverFailed";
import { ShareService } from "../../../src/services/ShareService";
import { PresentationExporter } from "../builder/configuration/PresentationExporter";

import BackgroundImages from "../../resources/BackgroundImages.json";
import { BuilderPreferences } from "../../entities/BuilderPreferences";
import { BuilderAsset, deserializeBuilderAsset } from "../../entities/BuilderAsset";
import { EditorHelper } from "../builder/editor/EditorHelper";

interface BackgroundImage {
	id: number;
	standardImagePath: string;
	standardImageThumbnailPath: string;
	widescreenImagePath: string;
	widescreenImageThumbnailPath: string;
	name: string;
	fontColor: string;
}

const images: BackgroundImage[] = BackgroundImages as any;

export const slideAspectRatios = [
	{
		id: "16x9",
		name: "16 x 9",
	},
	{
		id: "4x3",
		name: "4 x 3",
	},
];

const removeHtmlTagsRegex = /(<([^>]+)>)/gi;

export function DownloadSlides() {
	const params = useParams<{id: string}>();
	const [loading, setLoading] = useState(true);
	const [pageNotFound, setPageNotFound] = useState(false);
	const [serverFailed, setServerFailed] = useState(false);

	useEffect(() => {
		async function fetchData(){
			document.title = "Sermon Maker";

			const response = await ShareService.getSharedSlide(params.id.toLowerCase());
			if(response.error){
				if(response.statusCode === 404) return setPageNotFound(true);
				return setServerFailed(true);
			}

			const builderAsset = deserializeBuilderAsset(response.data.builderAsset);
			const preferences = response.data.builderPreferences;

			const preferredAspectRatio = builderAsset.slideExporterAspectRatio || "16x9";
			let fontColor = getFontColor(preferences, builderAsset);
			const description = builderAsset.text.replace(removeHtmlTagsRegex, " ").replace(/\s\s+/g, " ").trim();

			let existingMeta = document.querySelector("meta[name=description]") as HTMLMetaElement;
			if (!existingMeta) {
				let meta = document.createElement("meta");
				meta.name = "description";
				meta.content = description.substring(0, 160);
				document.getElementsByTagName("head")[0].appendChild(meta);
			} else {
				existingMeta.content = description.substring(0, 160);
			}

			document.title = `${builderAsset.name}`;

			let previewSlides = getPreviewSlidesFromEditorHtml(builderAsset.text);
	
			if (builderAsset.customSlideBackgroundImagePath) {
				fontColor = "#FFFFFF";
				const slideImageResponse = await ShareService.getCustomSlideBackgroundImageUrl(params.id.toLowerCase());
	
				if(slideImageResponse.success){
					PresentationExporter.exportWithImageData(previewSlides, preferredAspectRatio, fontColor, builderAsset.name, response.message);

				}
			} else {
				const selectedImage = images.find((image) => image.id === preferences.slideExporterSelectedGalleryImageId);
				const backgroundImage = selectedImage ? selectedImage : images[0];
				let image = null;
	
				if (builderAsset.slideExporterAspectRatio === "4x3") {
					image = backgroundImage.standardImagePath;
				} else {
					image = backgroundImage.widescreenImagePath;
				}
	
				PresentationExporter.exportWithImagePath(previewSlides, preferredAspectRatio, fontColor, builderAsset.name, image);
			}

			setLoading(false);
		};

		fetchData();
	}, [])

	const getFontColor = (preferences: BuilderPreferences, asset: BuilderAsset) => {
		const { slideExporterSelectedGalleryImageId } = preferences;
		if (asset.customSlideBackgroundImagePath) {
			return "#FFFFFF";
		}

		const selectedImage = images.find((image) => image.id === slideExporterSelectedGalleryImageId);
		const backgroundImage = selectedImage ? selectedImage : images[0];

		return backgroundImage.fontColor;
	};

	const getPreviewSlidesFromEditorHtml = (html: string) => {
		let editorSlides = EditorHelper.getEditorSlides(html);
		let previewSlides = EditorHelper.getPreviewSlides(editorSlides);
		return previewSlides;
	}

	if (pageNotFound) {
		return (
			<Grid container>
				<Grid item xs={12} sm={6} style={{ margin: "5% auto" }}>
					<Typography variant="h6" color="textSecondary" align="center">
						<ErrorIcon style={{ marginTop: "50px", width: "170px", height: "170px", color: "#CCC" }} />
						<br />
						Oops, it looks like something went wrong. This resource is no longer being shared by the owner.
						<br />
						Learn more about <a href="https://www.sermonmaker.net/">Sermon Maker</a>
					</Typography>
				</Grid>
			</Grid>
		);
	}

	if (serverFailed)
		return <ServerFailed />;

	if (loading) {
		return (
			<div style={{ textAlign: "center" }}>
				<CircularProgress style={{ marginTop: "50px" }} />
			</div>
		);
	}

	return null;
}